import { API_URL } from "../config";
import { onAuthenticationError, store } from "../redux";

export type MakeRequestError = {
  error: string;
};

export type MakeRequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: Record<string, unknown>;
  file?: File;
  files?: FileList;
  blob?: boolean;
  /**
   * Skip the Organization-Id header. Admin fleet surfaces need this: the
   * header NARROWS even super-admins to the selected org server-side, so a
   * cross-org action sent with it 404s.
   */
  omitOrganizationId?: boolean;
};

/** Error with the HTTP status attached so callers can branch (e.g. 409). */
export class RequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "RequestError";
  }
}

export const makeRequest = async <T>(url: string, options?: MakeRequestOptions): Promise<T> => {
  let headers = options?.headers || {};
  let bodyFormData = new FormData();
  let body: FormData | string | undefined = undefined;

  // Route the request to the client's currently-selected org. The server falls
  // back to the user's default when the header is absent (e.g. on the initial
  // session fetch before we know what the selection is).
  const state = store.getState();
  const { currentOrganizationId } = state.session;
  if (currentOrganizationId && !options?.omitOrganizationId) {
    headers["Organization-Id"] = currentOrganizationId;
  }

  if (options?.file || options?.files) {
    // Use FormData for file uploads
    body = bodyFormData;

    if (options?.file) {
      bodyFormData.append("file", options.file);
    }

    if (options?.files) {
      for (let i = 0; i < options.files.length; i++) {
        bodyFormData.append("file", options.files[i]);
      }
    }

    // Append body data to FormData if both file and body are provided
    if (options?.body) {
      Object.entries(options.body).forEach(([key, value]) => {
        bodyFormData.append(key, String(value));
      });
    }
  } else if (options?.body) {
    // Use JSON for body-only requests
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options?.body);
  }

  const response = await fetch(`${API_URL}${url}`, {
    method: options?.method || "GET",
    credentials: "include",
    headers,
    body,
  });

  if (response.status === 204) {
    return null as T;
  }

  if (response.status === 401 && url !== "/v1/auth/session") {
    onAuthenticationError();
  }

  if (options?.blob) {
    if (!response.ok) {
      const json = await response.json().catch(() => ({ error: "Request failed" }));
      throw new Error(json.error || `Request failed with status ${response.status}`);
    }
    return response.blob() as T;
  }

  const json = await response.json();
  if (response.ok) {
    return json as T;
  }

  const error = json as MakeRequestError & { detail?: unknown };

  // Most endpoints return {"error": ...}, but the fine-tuning proxy relays the control plane's
  // FastAPI-style {"detail": ...} verbatim (detail may be an object, e.g. eval-regression 409s).
  // Without the fallbacks a failed FT mutation would throw an Error with an empty message.
  const detail = typeof error.detail === "string" ? error.detail : error.detail != null ? JSON.stringify(error.detail) : undefined;

  throw new RequestError(error.error || detail || response.statusText || `Request failed with status ${response.status}`, response.status);
};
